import { useEffect, useState, useRef } from "react";
import { FaTimes, FaCloudUploadAlt, FaTrash, FaDownload, FaChevronDown } from "react-icons/fa";
import pathconfig from '../../../pathconfig.json';
import axios from "axios";
import Docx from "../../../images/icons/docx.png";
import Doc from "../../../images/icons/doc.png";
import Pdf from "../../../images/icons/pdf.png";
import Jpg from "../../../images/icons/jpg.png";
import Png from "../../../images/icons/png.png";
import Tif from "../../../images/icons/tif.png";
import Tiff from "../../../images/icons/tiff.png";
import Zip from "../../../images/icons/zip.png";
import Xml from "../../../images/icons/xml.png";
import Json from "../../../images/icons/json.png";
import Ini from "../../../images/icons/ini.png";
import Txt from "../../../images/icons/txt.png";
import Xlsx from "../../../images/icons/Xlsx.png";
import Noimage from "../../../images/icons/noimage.png";
import { toastError, toastSuccess } from '../../toaster';
import moment from 'moment';
import NodataFound from "../../nodata";
import packjson from '../../../../package.json';
import { useAppContextProvider } from '../../../contextapi';
import { getAttachmentFormatError, getAttachmentCategoryOptions } from '../../attachmentFormats';


// Display label for an Image tab file, mirroring the Image tab's own modal
// title (getImageTitle in imagelist.js) — e.g. "Fig. 4 (gr4.pdf)",
// "Inline Image (fx1.pdf)" — so the "Select Image to Replace" list matches
// what the Master Copier actually sees when they open that image.
const IMAGE_TYPE_MAP = {
  gr: "Graphical Image",
  ga: "Graphical Abstract Image",
  fx: "Inline Image",
  mmc: "Supplementary videos and gifs",
};
const getImageDisplayTitle = (name) => {
  if (!name) return "";
  const baseName = name.split(".")[0];
  const match = baseName.match(/^([a-zA-Z]+)(\d+)$/);
  const prefix = match?.[1]?.toLowerCase();
  const number = match?.[2];
  if (prefix === "gr") return `Fig. ${number} (${name})`;
  if (prefix === "sc") return `Scheme ${number}`;
  if (prefix === "pl") return `Plate ${number}`;
  const fullName = IMAGE_TYPE_MAP[prefix] || "";
  return fullName ? `${fullName} (${name})` : name;
};

const Attachment_Sidebar = (props) => {
  const [file, setFile] = useState([]);
  const fileInputRef = useRef(null);
  const closeBtnRef = useRef(null);
  const yesBtnRef = useRef(null);
  const firstCategoryRef = useRef(null);
  const [showconfirm, setConfirm] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  
  const [selectedFilesWithComment, setSelectedFilesWithComment] = useState([]);
  const [attachmentComment, setAttachmentComment] = useState([]);
  const [deleteData, setDeleteData] = useState('');
  const [loader, setLoader] = useState(true);
  const [imageFileList, setImageFileList] = useState([]);
  const [supplementaryFileList, setSupplementaryFileList] = useState([]);
  const [showValidationError, setShowValidationError] = useState(false);
  const [replaceDropdownOpenFile, setReplaceDropdownOpenFile] = useState(null);
  const replaceDropdownRef = useRef(null);
  const [imageTypeDropdownOpenFile, setImageTypeDropdownOpenFile] = useState(null);
  const imageTypeDropdownRef = useRef(null);
  const modalBodyRef = useRef(null);
  const { userProfile, setSidebarClose, alttextEnabled, fullData } = useAppContextProvider();


  useEffect(() => {
    if (props.fileItems) {
      console.log('File Items with Comments:', props.fileItems);
      setFile(props.fileItems);
      setLoader(false)
    }
  }, [props.fileItems]);

  // Fetch the list of images from the Image tab's folder so the "Replace Image"
  // dropdown can offer the exact existing image names to attach against.
  useEffect(() => {
    if (!fullData?.Content) return;
    const regex = /(?<!.)\\graphicspath\{\{([^}]*)\}\}/;
    const match = fullData.Content.match(regex);
    if (!match) return;
    const folderPath = match[1].replace(/\/$/, "");

    const getImageFileList = async () => {
      try {
        const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].get_image_details}`;
        const response = await axios.post(url, { file_path: folderPath });
        setImageFileList(response.data.Files || []);
      } catch (error) {
        console.log(error);
      }
    };
    getImageFileList();
  }, [fullData]);

  // Fetch the list of Supplementary tab files so the "Replace File" dropdown
  // (Media attachments) can offer the exact existing file names to attach against.
  useEffect(() => {
    if (!userProfile?.docid) return;

    const getSupplementaryFileList = async () => {
      try {
        const GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
        const GetDetailData = JSON.parse(GetDetailDataString);
        if (!GetDetailData?.Articledetails?.ArtResourcePath) return;

        const basePath = `${GetDetailData.Articledetails.ArtResourcePath}/Supplementary`;
        const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].retrievefiles}`;
        const response = await axios.post(url, { InputPath: basePath });

        // Mirror the Supplementary tab: only top-level files, and expose a
        // plain "name" (like imageFileList's shape) for the dropdown to use.
        // System manifest files (manifest-in.json, ...) are not offered to the Author.
        const isAuthorRole = userProfile?.role === "Author";
        const filteredFiles = (response.data || [])
          .filter((f) => !f.path.replace(basePath + "/", "").includes("/"))
          .map((f) => ({ name: f.path.split('/').pop() }))
          .filter((f) => !(isAuthorRole && /^manifest/i.test(f.name)));

        setSupplementaryFileList(filteredFiles);
      } catch (error) {
        console.log(error);
      }
    };
    getSupplementaryFileList();
  }, [userProfile]);

  // Close the "Select Image/File to Replace" custom dropdown on outside click or Escape
  useEffect(() => {
    if (!replaceDropdownOpenFile) return;
    const handleClickOutside = (e) => {
      if (replaceDropdownRef.current && !replaceDropdownRef.current.contains(e.target)) {
        setReplaceDropdownOpenFile(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setReplaceDropdownOpenFile(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [replaceDropdownOpenFile]);

  // Close the "Image Type" custom dropdown on outside click or Escape
  useEffect(() => {
    if (!imageTypeDropdownOpenFile) return;
    const handleClickOutside = (e) => {
      if (imageTypeDropdownRef.current && !imageTypeDropdownRef.current.contains(e.target)) {
        setImageTypeDropdownOpenFile(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setImageTypeDropdownOpenFile(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [imageTypeDropdownOpenFile]);

  // Scroll `el` inside the comment modal's body: "nearest" brings it fully
  // into view with the least movement, "center" puts it mid-body.
  const scrollInModalBody = (el, mode) => {
    const body = modalBodyRef.current;
    if (!body || !el || !body.contains(el)) return;
    const b = body.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const margin = 12;
    let delta = 0;
    if (mode === 'center') {
      delta = r.top + r.height / 2 - (b.top + b.height / 2);
    } else if (r.bottom > b.bottom - margin) {
      // Never push the top of the element above the body's top edge.
      delta = Math.min(r.bottom - b.bottom + margin, r.top - b.top - margin);
    } else if (r.top < b.top + margin) {
      delta = r.top - b.top - margin;
    }
    if (delta) body.scrollBy({ top: delta, behavior: 'smooth' });
  };

  // An opened dropdown list can run past the bottom of the modal body —
  // scroll it into view.
  useEffect(() => {
    const container = imageTypeDropdownOpenFile
      ? imageTypeDropdownRef.current
      : replaceDropdownOpenFile
        ? replaceDropdownRef.current
        : null;
    const list = container?.querySelector('.att-custom-select-list');
    if (list) scrollInModalBody(list, 'nearest');
  }, [imageTypeDropdownOpenFile, replaceDropdownOpenFile]);

  // Once a choice is made, center the field that follows it (it may only
  // appear after this choice, so wait for the re-render).
  const centerNextField = (fromEl) => {
    const group = fromEl?.closest('.att-form-group');
    if (!group) return;
    setTimeout(() => {
      let next = group.nextElementSibling;
      while (next && !next.classList.contains('att-form-group')) next = next.nextElementSibling;
      if (next) scrollInModalBody(next, 'center');
    }, 0);
  };

  // Auto-focus close button whenever sidebar is opened (from layout)
  useEffect(() => {
        if (props.leftsidebarvalue === 'attachement') {
                if (closeBtnRef.current) {
                    closeBtnRef.current.focus();
                }
        }
    }, [props.leftsidebarvalue])
    
  useEffect(() => {
    if (showconfirm && yesBtnRef.current) {
      yesBtnRef.current.focus();
    }
  }, [showconfirm]);

    // Close confirmation modal on ESC key
    useEffect(() => {
      if (!showconfirm) return;
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          setConfirm(false);
        }
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }, [showconfirm]);
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles || selectedFiles.length === 0) {
      // No files selected, do nothing and ensure modal does not open
      setShowCommentModal(false);
      setSelectedFilesWithComment([]);
      setAttachmentComment([]);
      return;
    }
    const selectedFiles2 = file.filter((a) =>
      selectedFiles.some((sf) => sf.name === a.Filename)
    );
    if (selectedFiles2.length !== 0) {
      toastError('File is already exit.');
      e.target.value = null;
    } else {
      setSelectedFilesWithComment(selectedFiles);
      setShowCommentModal(true);
    }
  }
useEffect(() => {
  if (showCommentModal) {
    setTimeout(() => {
      if (firstCategoryRef.current) {
        firstCategoryRef.current.focus();
      }
    }, 50);
  }
}, [showCommentModal]);



  const handleUploadWithComment = () => {
    // Safety net: block submission if an Image/Media/Text attachment somehow
    // ended up with an unsupported file extension (the category dropdown
    // already prevents this).
    for (const file of selectedFilesWithComment) {
      const fileMetadata = attachmentComment.find(item => item.file === file.name);
      const formatError = getAttachmentFormatError(fileMetadata?.filetype, file.name, alttextEnabled);
      if (formatError) {
        toastError(formatError);
        return;
      }
    }

    // Check if all selected files have a type and comment
    const missingFields = selectedFilesWithComment.filter(file => {
      const fileMetadata = attachmentComment.find(item => item.file === file.name);
      const isImage = fileMetadata?.filetype === 'imageAttachment';
      const isMedia = fileMetadata?.filetype === 'mediaAttachment';
      const baseInvalid = !fileMetadata || !fileMetadata.filetype || !fileMetadata.comment;
      const altTextInvalid = alttextEnabled && isImage && !fileMetadata?.altTextGeneration;
      // Image Type / File Type (New vs Replace) is only shown when alttextEnabled —
      // no field to fill in, so nothing to require, when it's off.
      const imageActionInvalid = alttextEnabled && isImage && (!fileMetadata?.imageAction || (fileMetadata.imageAction === 'replace' && !fileMetadata.replaceImageName));
      const mediaActionInvalid = alttextEnabled && isMedia && (!fileMetadata?.mediaAction || (fileMetadata.mediaAction === 'replace' && !fileMetadata.replaceMediaName));
      return baseInvalid || altTextInvalid || imageActionInvalid || mediaActionInvalid;
    });

    if (missingFields.length > 0) {
      setShowValidationError(true);
      toastError("A required field is missing. Please fill out all required fields and try again.");
      return; // Stop the upload if validation fails
    }
    setShowValidationError(false);
    // All validation passed. Trim and sanitize comments at submit time before upload.
    const sanitizeComment = (value) => {
      if (typeof value !== 'string') return '';
      // Remove HTML tags to prevent stored HTML injection
      return value.replace(/<[^>]*>/gm, '').trim();
    };
    
    setAttachmentComment(prev =>
      prev.map(item => ({ ...item, comment: sanitizeComment(item.comment) }))
    );
    // Ensure irrelevant fields (altTextGeneration/imageAction/replaceImageName/
    // mediaAction/replaceMediaName) are set to null for non-matching categories
    const updatedComments = selectedFilesWithComment.map((file) => {
      const existingComment = attachmentComment.find(item => item.file === file.name) || {};
      const isImage = existingComment.filetype === 'imageAttachment';
      const isMedia = existingComment.filetype === 'mediaAttachment';
      return {
        ...existingComment,
        file: file.name,
        comment: sanitizeComment(existingComment.comment || ''),
        altTextGeneration: isImage ? existingComment.altTextGeneration : null,
        imageAction: isImage ? existingComment.imageAction : null,
        replaceImageName: isImage && existingComment.imageAction === 'replace' ? existingComment.replaceImageName : null,
        mediaAction: isMedia ? existingComment.mediaAction : null,
        replaceMediaName: isMedia && existingComment.mediaAction === 'replace' ? existingComment.replaceMediaName : null
      };
    });
    proceedWithUpload(updatedComments);
  };

  const handleAltTextChange = (filename, value) => {
    setAttachmentComment(prev =>
      prev.map(item => (item.file === filename ? { ...item, altTextGeneration: value } : item))
    );
  };

  const proceedWithUpload = (updatedComments) => {
    setLoader(true);
    props.getTrigger(false);

    try {
      let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
      let GetDetailData = JSON.parse(GetDetailDataString);
      var formdata = new FormData();

      // Add base data
      formdata.append("jobGUID", GetDetailData.Articledetails.ArticleGUID);
      formdata.append("ActivityID", GetDetailData.ActivtyDetails.ActivityID);
      formdata.append("docid", userProfile.docid);
      formdata.append("type", "attachment");

      // Validate file sizes and append data
      const oversizedFiles = [];
      const maxFileSize = 40 * 1024 * 1024; // 40MB

      // Index the backend sees: only files actually appended are counted, so a
      // skipped oversized file must not leave a gap in the metadata arrays.
      let appendIndex = 0;

      selectedFilesWithComment.forEach((file) => {
        if (file.size > maxFileSize) {
          oversizedFiles.push(file.name);
          return;
        }

        const fileMetadata = updatedComments.find(item => item.file === file.name) || {};

        formdata.append("files", file);
        formdata.append(`fileTypes[${appendIndex}]`, fileMetadata.filetype);
        formdata.append(`fileComments[${appendIndex}]`, fileMetadata.comment);
        formdata.append(`altTextGeneration[${appendIndex}]`, alttextEnabled ? fileMetadata.altTextGeneration : null);
        formdata.append(`imageAction[${appendIndex}]`, fileMetadata.imageAction || null);
        formdata.append(`replaceImageName[${appendIndex}]`, fileMetadata.replaceImageName || null);
        formdata.append(`mediaAction[${appendIndex}]`, fileMetadata.mediaAction || null);
        formdata.append(`replaceMediaName[${appendIndex}]`, fileMetadata.replaceMediaName || null);
        appendIndex += 1;
      });

      if (oversizedFiles.length > 0) {
        toastError(`File size should be less than 40 MB: ${oversizedFiles.join(', ')}`);
        if (oversizedFiles.length === selectedFilesWithComment.length) {
          setLoader(false);
          setShowCommentModal(false);
          setSelectedFilesWithComment([]);
          setAttachmentComment([]);
          return;
        }
      }

      const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].upload_attachment}`;

      axios.post(url, formdata, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          setLoader(false);
          props.refreshAttachments(true);
          setShowCommentModal(false);
          setSelectedFilesWithComment([]);
          setAttachmentComment([]);
          // Reset file input to allow re-selection of the same file
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          if (response.data.SuccessFiles.length !== 0) {
            response.data.SuccessFiles.forEach(item =>
              toastSuccess(`${item.data[0].Filename} - ${item.message}`)
            );
          }
          if (response.data.FailedFiles.length !== 0) {
            response.data.FailedFiles.forEach(item =>
              toastError(`${item.data[0].Filename} - ${item.message}`)
            );
          }
        })
        .catch(error => {
          console.log(error);
          // Surface the backend's message (e.g. "This file format is restricted…")
          // which is returned as { error: "…" }; fall back to the generic message.
          toastError(error.response?.data?.error || error.message);
          setLoader(false);
        });

    } catch (error) {
      console.log(error);
      toastError(error.message);
      setLoader(false);
    }
  };



  const deleteConfirm = (value) => {
    setDeleteData(value)
    setConfirm(true)
  }

  const deleteAttchment = () => {
    props.getTrigger(false)
    let dataValue = deleteData;
    let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
    let GetDetailData = JSON.parse(GetDetailDataString);
    let data = {
      "ArticleGUID": dataValue.ArticleGUID,
      "AttachID": dataValue.AttachID,
      "Proof_Type": GetDetailData.jobdetails.ProofType,
      "AttachFlag": dataValue.AttachFlag,
      "docid": userProfile.docid
    }
    const deleteUrl = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].deleteAttachment}`
    axios.post(deleteUrl, data).then((response) => {
      setConfirm(false)
      props.getTrigger(true)
      props.refreshAttachments(); 
      toastSuccess(`${response.data.data[0].Filename} - ${response.data.message}`)
    }
    ).catch((error) => {
      console.log(error);
      toastError(error.message);
    })
  }

  function getFileIcon(filetype) {
    switch (filetype) {
      case "png": return Png
      case "pdf": return Pdf
      case "jpg":
      case "jpeg": return Jpg
      case "docx": return Docx
      case "doc": return Doc
      case "tiff": return Tiff
      case "tif": return Tif
      case "zip": return Zip
      case "txt": return Txt
      case "xlsx": return Xlsx
      case "json": return Json
      case "xml": return Xml
      case "ini": return Ini
      default: return Noimage
    }
  }

  const handleSelectType = (value, filename, index) => {
    const formatError = getAttachmentFormatError(value, filename, alttextEnabled);
    if (formatError) {
      toastError(formatError);
      return;
    }
    const isImage = value === 'imageAttachment';
    const isMedia = value === 'mediaAttachment';
    const existing = attachmentComment.find(it => it.file === filename);
    let newComment = {
      file: filename,
      filetype: value,
      comment: existing?.comment || "",
      altTextGeneration: isImage ? (existing?.altTextGeneration || '') : null,
      imageAction: isImage ? (existing?.imageAction || '') : null,
      replaceImageName: isImage ? (existing?.replaceImageName || '') : null,
      mediaAction: isMedia ? (existing?.mediaAction || '') : null,
      replaceMediaName: isMedia ? (existing?.replaceMediaName || '') : null
    };

    // Check if the file is already present
    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newComment]);
    } else {
      // Optional: Update the existing entry if needed
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename
            ? {
                ...item,
                filetype: value,
                altTextGeneration: isImage ? (item.altTextGeneration || '') : null,
                imageAction: isImage ? (item.imageAction || '') : null,
                replaceImageName: isImage ? (item.replaceImageName || '') : null,
                mediaAction: isMedia ? (item.mediaAction || '') : null,
                replaceMediaName: isMedia ? (item.replaceMediaName || '') : null
              }
            : item
        )
      );
    }
  };

  // Handler for the "New Image" / "Replace Image" choice
  const handleImageActionChange = (filename, value) => {
    const existing = attachmentComment.find(it => it.file === filename);
    const newEntry = {
      file: filename,
      filetype: existing?.filetype || 'imageAttachment',
      comment: existing?.comment || '',
      // "New Image" always needs fresh alt-text, so auto-select the
      // "completely new image" option (the other one is disabled in the UI).
      // "Replace Image" clears the choice so the user picks explicitly.
      altTextGeneration: value === 'new' ? 'yes' : '',
      imageAction: value,
      // Selecting "New Image" clears any previously chosen image to replace
      replaceImageName: value === 'replace' ? (existing?.replaceImageName || '') : ''
    };

    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newEntry]);
    } else {
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename
            ? {
                ...item,
                imageAction: value,
                altTextGeneration: value === 'new' ? 'yes' : '',
                replaceImageName: value === 'replace' ? (item.replaceImageName || '') : ''
              }
            : item
        )
      );
    }
  };

  // Handler for picking the exact existing image being replaced
  const handleReplaceImageNameChange = (filename, value) => {
    setAttachmentComment(prev =>
      prev.map(item => (item.file === filename ? { ...item, replaceImageName: value } : item))
    );
  };

  // Handler for the "New File" / "Replace File" choice (media attachments)
  const handleMediaActionChange = (filename, value) => {
    const existing = attachmentComment.find(it => it.file === filename);
    const newEntry = {
      file: filename,
      filetype: existing?.filetype || 'mediaAttachment',
      comment: existing?.comment || '',
      mediaAction: value,
      // Selecting "New File" clears any previously chosen file to replace
      replaceMediaName: value === 'replace' ? (existing?.replaceMediaName || '') : ''
    };

    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newEntry]);
    } else {
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename
            ? { ...item, mediaAction: value, replaceMediaName: value === 'replace' ? (item.replaceMediaName || '') : '' }
            : item
        )
      );
    }
  };

  // Handler for picking the exact existing supplementary file being replaced
  const handleReplaceMediaNameChange = (filename, value) => {
    setAttachmentComment(prev =>
      prev.map(item => (item.file === filename ? { ...item, replaceMediaName: value } : item))
    );
  };

  // Function to handle adding or updating a comment for a filename
  const handleAddComment = (comment, filename, index) => {
    const existing = attachmentComment.find(item => item.file === filename);
    const isImage = existing?.filetype === 'imageAttachment';
    const isMedia = existing?.filetype === 'mediaAttachment';
    let newComment = {
      file: filename,
      filetype: existing?.filetype || '',
      comment: comment || "",
      altTextGeneration: existing?.altTextGeneration !== undefined ? existing.altTextGeneration : (isImage ? '' : null),
      imageAction: existing?.imageAction !== undefined ? existing.imageAction : (isImage ? '' : null),
      replaceImageName: existing?.replaceImageName !== undefined ? existing.replaceImageName : (isImage ? '' : null),
      mediaAction: existing?.mediaAction !== undefined ? existing.mediaAction : (isMedia ? '' : null),
      replaceMediaName: existing?.replaceMediaName !== undefined ? existing.replaceMediaName : (isMedia ? '' : null)
    };

    // Check if the file is already present
    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newComment]);
    } else {
      // Optional: Update the existing entry if needed
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename ? { ...item, comment: comment } : item
        )
      );
    }
  };

  const RoleSplit = (value) => {
    const roleArray = JSON.parse(value);
    const roleName = roleArray[0].RoleName;
    return roleName;
  }

  const getAttachmentTypeDisplay = (type) => {
    switch (type) {
      case 'imageAttachment':
        return 'Image Attachment'
      case 'textAttachment':
        return 'Text Attachment'
      case 'commentAttachment':
        return 'Comment Attachment'
      case 'mediaAttachment':
        return 'Media Attachment'
      case 'otherAttachment':
        return 'Other Attachment'
      default:
        return type
    }
  }

  return (
    <div className="attachement_area">
      <main className="slider-header">
        <h2>Attachement</h2>
        <div>
          <button
            className="close-btn"
            title="Close"
            tabIndex={0}
            ref={closeBtnRef}
            onClick={() => {
              setSidebarClose(true);
              // After closing, focus the Attachement tab button
              setTimeout(() => {
                const attBtn = document.querySelector('button[title="Attachement Tab"]');
                if (attBtn) attBtn.focus();
              }, 0);
            }}
            onKeyDown={(e) => {
              // Support Enter/Space to activate close and then restore focus
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSidebarClose(true);
                setTimeout(() => {
                  const attBtn = document.querySelector('button[title="Attachement Tab"]');
                  if (attBtn) attBtn.focus();
                }, 0);
              }
            }}
          >
            <FaTimes />
          </button>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <div className={`modal-outer ${showconfirm ? "active" : ""}`}>
        <div className="modal confirmation-popup">
          <div className="modal-header">
            <h1>Confirm Delete</h1>
            <button className="modal-close-btn" tabIndex={0} aria-label="Close" onClick={() => setConfirm(false)}><FaTimes /></button>
          </div>
          <div className="modal-body">
            <p style={{ textAlign: 'center', margin: '10px 0' }}>Are you sure you want to delete this file?</p>
          </div>
          <div className="modal-footer">
            <button
              className="primary"
              tabIndex={0}
              ref={yesBtnRef}
              onClick={deleteAttchment}
            >Yes</button>
            <button
              className="outline-secondary"
              tabIndex={0}
              onClick={() => setConfirm(false)}
            >No</button>
          </div>
        </div>
      </div>
      {/* Comment Modal - Alternative UI */}
      <div className={`modal-outer ${showCommentModal ? "active" : ""}`}>
        <div className="modal att-comment-modal">
          {/* Header */}
          <div className="modal-header">
            <h1>Add Comment for Attachment</h1>
            <button
              className="modal-close-btn"
              tabIndex={0}
              aria-label="Close"
              onClick={() => {
                setShowCommentModal(false);
                setSelectedFilesWithComment([]);
                setAttachmentComment([]);
                setShowValidationError(false);
                if (fileInputRef.current) {
                  fileInputRef.current.value = null;
                }
              }}
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body att-modal-body" ref={modalBodyRef}>
            {selectedFilesWithComment && (
              <>
                {Array.from(selectedFilesWithComment).map((file, index) => {
                  const meta = attachmentComment.find(it => it.file === file.name);
                  const isImage = meta?.filetype === 'imageAttachment';
                  const isMedia = meta?.filetype === 'mediaAttachment';
                  const categoryInvalid = showValidationError && !meta?.filetype;
                  const commentInvalid = showValidationError && !meta?.comment;
                  const imageActionInvalid = showValidationError && alttextEnabled && isImage && !meta?.imageAction;
                  const replaceImageInvalid = showValidationError && alttextEnabled && isImage && meta?.imageAction === 'replace' && !meta?.replaceImageName;
                  const altTextInvalid = showValidationError && alttextEnabled && isImage && !meta?.altTextGeneration;
                  const mediaActionInvalid = showValidationError && alttextEnabled && isMedia && !meta?.mediaAction;
                  const replaceMediaInvalid = showValidationError && alttextEnabled && isMedia && meta?.mediaAction === 'replace' && !meta?.replaceMediaName;
                  return (
                  <div className="att-file-card" key={index}>
                    <div className="att-file-card-header">
                      <div className="att-file-icon">
                        {file.name.split('.').pop().substring(0, 2).toUpperCase()}
                      </div>
                      <span title={file.name}>{file.name}</span>
                    </div>
                    <div className="att-file-card-body">
                      {/* Category */}
                      <div className="att-form-group">
                        <label className="att-form-label">
                          Category <span className="att-required">*</span>
                        </label>
                        <select
                          ref={index === 0 ? firstCategoryRef : null}
                          value={attachmentComment.find(it => it.file === file.name)?.filetype || ''}
                          onChange={(e) => {
                            handleSelectType(e.target.value, file.name, index);
                            centerNextField(e.target);
                          }}
                          className={`att-select ${categoryInvalid ? 'att-invalid' : ''}`}
                          required
                          tabIndex={0}
                        >
                          <option value="">-- Choose a category --</option>
                          {getAttachmentCategoryOptions(alttextEnabled).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* New Image / Replace Image choice (shown for image attachments
                          only when alttextEnabled). Custom listbox (not a native
                          <select>) so the labels can be bold. */}
                      {alttextEnabled && attachmentComment.find(it => it.file === file.name)?.filetype === 'imageAttachment' && (() => {
                        const currentImageAction = attachmentComment.find(it => it.file === file.name)?.imageAction || '';
                        const imageActionOptions = [
                          { value: 'new', label: 'New Image', description: 'Adding a new figure entirely (never existed before)' },
                          { value: 'replace', label: 'Replace Image', description: "Swapping an existing figure's file (updates a current one)" },
                        ];
                        const currentOption = imageActionOptions.find(opt => opt.value === currentImageAction);
                        return (
                          <div className="att-form-group">
                            <label className="att-form-label">
                              Image Type <span className="att-required">*</span>
                            </label>
                            <div
                              className="att-custom-select"
                              ref={imageTypeDropdownOpenFile === file.name ? imageTypeDropdownRef : null}
                            >
                              <button
                                type="button"
                                className={`att-custom-select-trigger ${imageActionInvalid ? 'att-invalid' : ''}`}
                                onClick={() =>
                                  setImageTypeDropdownOpenFile(prev => (prev === file.name ? null : file.name))
                                }
                                tabIndex={0}
                              >
                                <span>
                                  {currentOption ? (
                                    <><strong>{currentOption.label}</strong> – {currentOption.description}</>
                                  ) : '-- Choose an option --'}
                                </span>
                                <FaChevronDown className="att-custom-select-arrow" />
                              </button>
                              {imageTypeDropdownOpenFile === file.name && (
                                <ul className="att-custom-select-list" role="listbox">
                                  {imageActionOptions.map((opt) => {
                                    const isSelected = currentImageAction === opt.value;
                                    return (
                                      <li
                                        key={opt.value}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                        onClick={(e) => {
                                          handleImageActionChange(file.name, opt.value);
                                          setImageTypeDropdownOpenFile(null);
                                          centerNextField(e.currentTarget);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleImageActionChange(file.name, opt.value);
                                            setImageTypeDropdownOpenFile(null);
                                            centerNextField(e.currentTarget);
                                          }
                                        }}
                                      >
                                        <strong>{opt.label}</strong> – {opt.description}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Existing image name picker, only when alttextEnabled and
                          "Replace Image" is chosen. Uses a custom listbox (not a
                          native <select>) because a native dropdown's popup height
                          can't be constrained via CSS. */}
                      {alttextEnabled &&
                        attachmentComment.find(it => it.file === file.name)?.filetype === 'imageAttachment' &&
                        attachmentComment.find(it => it.file === file.name)?.imageAction === 'replace' && (
                        <div className="att-form-group">
                          <label className="att-form-label">
                            Select Image to Replace <span className="att-required">*</span>
                          </label>
                          <div
                            className="att-custom-select"
                            ref={replaceDropdownOpenFile === file.name ? replaceDropdownRef : null}
                          >
                            <button
                              type="button"
                              className={`att-custom-select-trigger ${replaceImageInvalid ? 'att-invalid' : ''}`}
                              onClick={() =>
                                setReplaceDropdownOpenFile(prev => (prev === file.name ? null : file.name))
                              }
                              tabIndex={0}
                            >
                              <span>
                                {getImageDisplayTitle(attachmentComment.find(it => it.file === file.name)?.replaceImageName) || '-- Select image --'}
                              </span>
                              <FaChevronDown className="att-custom-select-arrow" />
                            </button>
                            {replaceDropdownOpenFile === file.name && (
                              <ul className="att-custom-select-list" role="listbox">
                                {[...imageFileList]
                                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                  .map((img, imgIndex) => {
                                    const isSelected = attachmentComment.find(it => it.file === file.name)?.replaceImageName === img.name;
                                    return (
                                      <li
                                        key={imgIndex}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                        onClick={(e) => {
                                          handleReplaceImageNameChange(file.name, img.name);
                                          setReplaceDropdownOpenFile(null);
                                          centerNextField(e.currentTarget);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleReplaceImageNameChange(file.name, img.name);
                                            setReplaceDropdownOpenFile(null);
                                            centerNextField(e.currentTarget);
                                          }
                                        }}
                                      >
                                        {getImageDisplayTitle(img.name)}
                                      </li>
                                    );
                                  })}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}

                      {/* New File / Replace File choice (shown for media attachments
                          only when alttextEnabled) */}
                      {alttextEnabled && attachmentComment.find(it => it.file === file.name)?.filetype === 'mediaAttachment' && (
                        <div className="att-form-group">
                          <label className="att-form-label">
                            File Type <span className="att-required">*</span>
                          </label>
                          <select
                            value={attachmentComment.find(it => it.file === file.name)?.mediaAction || ''}
                            onChange={(e) => {
                              handleMediaActionChange(file.name, e.target.value);
                              centerNextField(e.target);
                            }}
                            className={`att-select ${mediaActionInvalid ? 'att-invalid' : ''}`}
                            required
                            tabIndex={0}
                          >
                            <option value="">-- Choose an option --</option>
                            <option value="new">New File</option>
                            <option value="replace">Replace File</option>
                          </select>
                        </div>
                      )}

                      {/* Existing supplementary file name picker, only when alttextEnabled
                          and "Replace File" is chosen. Uses the same custom listbox as
                          "Replace Image" so the popup height stays capped. */}
                      {alttextEnabled &&
                        attachmentComment.find(it => it.file === file.name)?.filetype === 'mediaAttachment' &&
                        attachmentComment.find(it => it.file === file.name)?.mediaAction === 'replace' && (
                        <div className="att-form-group">
                          <label className="att-form-label">
                            Select File to Replace <span className="att-required">*</span>
                          </label>
                          <div
                            className="att-custom-select"
                            ref={replaceDropdownOpenFile === file.name ? replaceDropdownRef : null}
                          >
                            <button
                              type="button"
                              className={`att-custom-select-trigger ${replaceMediaInvalid ? 'att-invalid' : ''}`}
                              onClick={() =>
                                setReplaceDropdownOpenFile(prev => (prev === file.name ? null : file.name))
                              }
                              tabIndex={0}
                            >
                              <span>
                                {attachmentComment.find(it => it.file === file.name)?.replaceMediaName || '-- Select file --'}
                              </span>
                              <FaChevronDown className="att-custom-select-arrow" />
                            </button>
                            {replaceDropdownOpenFile === file.name && (
                              <ul className="att-custom-select-list" role="listbox">
                                {[...supplementaryFileList]
                                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                  .map((suppFile, suppIndex) => {
                                    const isSelected = attachmentComment.find(it => it.file === file.name)?.replaceMediaName === suppFile.name;
                                    return (
                                      <li
                                        key={suppIndex}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                        onClick={(e) => {
                                          handleReplaceMediaNameChange(file.name, suppFile.name);
                                          setReplaceDropdownOpenFile(null);
                                          centerNextField(e.currentTarget);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleReplaceMediaNameChange(file.name, suppFile.name);
                                            setReplaceDropdownOpenFile(null);
                                            centerNextField(e.currentTarget);
                                          }
                                        }}
                                      >
                                        {suppFile.name}
                                      </li>
                                    );
                                  })}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Alt text options for images (only when alttextEnabled) */}
                      {alttextEnabled && userProfile.role === "Author" && attachmentComment.find(it => it.file === file.name)?.filetype === 'imageAttachment' && (() => {
                        const altEntry = attachmentComment.find(it => it.file === file.name);
                        // "New Image" always needs fresh alt-text: option 1 is
                        // auto-selected (see handleImageActionChange) and
                        // option 2 is disabled.
                        const isNewImage = altEntry?.imageAction === 'new';
                        return (
                        <div className="att-form-group">
                          <div className={`att-alt-section ${altTextInvalid ? 'att-invalid' : ''}`}>
                            <div className="att-alt-header">
                              {/* <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9-3a1 1 0 11-2 0 1 1 0 012 0zM6.75 7.75a.75.75 0 000 1.5h.5V11a.75.75 0 001.5 0V8.5a.75.75 0 00-.75-.75h-1.25z" fill="#333"/></svg> */}
                              Alternative text option <span className="att-required">*</span>
                            </div>
                            <div className="att-alt-body">
                              <label
                                className={`att-alt-option ${altEntry?.altTextGeneration === 'yes' ? 'att-selected' : ''}`}
                              >
                                <input
                                  type="radio"
                                  name={`alt-${index}`}
                                  value="yes"
                                  checked={altEntry?.altTextGeneration === 'yes'}
                                  onChange={(e) => {
                                    handleAltTextChange(file.name, 'yes');
                                    centerNextField(e.target);
                                  }}
                                  required
                                />
                                <div className="att-alt-option-text">
                                  <strong>Add/replace with a completely new image</strong>
                                  <span>A new alt-text needs to be generated.</span>
                                </div>
                              </label>

                              <label
                                className={`att-alt-option ${altEntry?.altTextGeneration === 'no' ? 'att-selected' : ''}`}
                                style={isNewImage ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                aria-disabled={isNewImage}
                                title={isNewImage ? 'Not applicable for a new image — new alt-text is always generated.' : undefined}
                              >
                                <input
                                  type="radio"
                                  name={`alt-${index}`}
                                  value="no"
                                  checked={altEntry?.altTextGeneration === 'no'}
                                  disabled={isNewImage}
                                  onChange={(e) => {
                                    handleAltTextChange(file.name, 'no');
                                    centerNextField(e.target);
                                  }}
                                />
                                <div className="att-alt-option-text">
                                  <strong>Upload the same image in higher resolution</strong>
                                  <span>Retain the previously generated alt-text</span>
                                </div>
                              </label>

                              <div className="att-info-banner">
                                <svg className="att-info-icon" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9-3a1 1 0 11-2 0 1 1 0 012 0zM6.75 7.75a.75.75 0 000 1.5h.5V11a.75.75 0 001.5 0V8.5a.75.75 0 00-.75-.75h-1.25z" fill="#0c5aa8"/></svg>
                                <span>If only the resolution changed (low to high), new alt text may not be required.</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })()}

                      {/* Comment — moved to the bottom of the card, after all
                          type-specific fields */}
                      <div className="att-form-group">
                        <label className="att-form-label">
                          Comment <span className="att-required">*</span>
                        </label>
                        <textarea
                          value={attachmentComment.find(it => it.file === file.name)?.comment || ''}
                          onChange={(e) => handleAddComment(e.target.value, file.name)}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Enter comment for attachment"
                          required
                          className={`att-textarea ${commentInvalid ? 'att-invalid' : ''}`}
                          tabIndex={0}
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer att-modal-footer">
            {showValidationError && (
              <p className="att-validation-banner" role="alert">
                Please fill all the required fields.
              </p>
            )}
            <div className="att-modal-footer-actions">
              <button
                className="primary"
                tabIndex={0}
                onClick={handleUploadWithComment}
              >
                Upload
              </button>
              <button
                className="outline-secondary"
                tabIndex={0}
                onClick={() => {
                  setShowCommentModal(false);
                  setSelectedFilesWithComment([]);
                  setAttachmentComment([]);
                  setShowValidationError(false);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                  }
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      
      <div className="upload-area">
        <button
          className="upload-btn-wrapper"
          aria-label="File Upload"
        onKeyDown={e => {
          if (e.key === 'Tab' && !e.shiftKey && file.length === 0) {
            e.preventDefault();
            const tocBtn = document.getElementById('toc-tab-btn');
            if (tocBtn) tocBtn.focus();
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (fileInputRef.current) fileInputRef.current.click();
            return;
          }
        }}
        >
          <label htmlFor="file-upload" style={{ display: 'contents' }}>
            <FaCloudUploadAlt aria-hidden="true" />
            <p>Choose file or drag here</p>
          </label>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileSelect}
            multiple
            ref={fileInputRef}
          />
        </button>

        <section className="upload-list-area" aria-label="Uploaded files list">
          {loader ? (
            <div className="fileupload-loder">
              <span className="loader" aria-hidden="true"></span>
            </div>
          ) : null}
          {file.length !== 0 ? (
            file.map((item, index) => (
              <div className="upload-list-area-inner" key={index}>
                <div className="icons-details">
                  <div className="upload-icons">
                    <img src={getFileIcon(item.filetype)} alt={item.filetype} />
                  </div>
                  <div className="upload-details" style={{lineHeight: "20px"}}>
                    <p aria-label={`filename:${item.Filename}`} tabIndex={0} title={item.Filename}>{item.Filename}</p>
                    <label aria-label={`username:${item.username}`} tabIndex={0}>{item.username}</label>
                    <span>
                      Date: {moment(item.CreatedOn).format("DD MMM YY")}, Size: {item.Filesize}
                    </span>
                    <a
                      href={item.SASFilepath}
                      download={item.Filename}
                      className="close-btn download-btn"
                      title="Download"
                      style={{ right: "21px" }}
                      onClick={() => toastSuccess(`${item.Filename} downloaded successfully`)}
                      tabIndex={0}
                    >
                      <FaDownload aria-hidden="true"/>
                    </a>

                    {RoleSplit(item.Rolename) === userProfile?.role && (
                      <button
                        className="close-btn attach-del-btn"
                        title="delete"
                        tabIndex={0}
                        onClick={() => deleteConfirm(item)}
                      >
                        <FaTrash aria-hidden="true"/>
                      </button>
                    )}
                  </div>
                  {/* {item.comment && (
                  <div className="attachment-comment">
                    <strong>Comment:</strong> {item.comment}
                  </div>
                )} */}
                </div>
                <div className="attachment-comment">
                  <p aria-label={`attachment type:${item.AttachType}`} tabIndex={0}><label>Type: </label>{getAttachmentTypeDisplay(item.AttachType)}</p>
             {item.Remarks && item.Remarks.length !== 0 ? (
                  <p
                    aria-label={`attachment comment:${item.Remarks}`}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        // Try to focus the TOC tab by id
                        const tocBtn = document.getElementById('toc-tab-btn');
                        if (tocBtn) tocBtn.focus();
                      }
                    }}
                  >
                    <label>Comment:</label>
                    <span dangerouslySetInnerHTML={{ __html: item.Remarks }} />
                  </p>
                ) : null}
                {alttextEnabled && item.altTextGeneration !== "null" ? (
                  <p aria-label={`alternative text generation:${item.altTextGeneration}`} tabIndex={0}>
                    <label>Alt Text Generation:</label>
                    <span>{item.altTextGeneration === 'yes' ? 'Yes' : 'No'}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'imageAttachment' && item.imageAction && item.imageAction !== 'null' ? (
                  <p aria-label={`image type:${item.imageAction}`} tabIndex={0}>
                    <label>Image Type:</label>
                    <span>{item.imageAction === 'replace' ? 'Replace Image' : 'New Image'}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'imageAttachment' && item.imageAction === 'replace' && item.replaceImageName && item.replaceImageName !== 'null' ? (
                  <p aria-label={`image replaced:${item.replaceImageName}`} tabIndex={0}>
                    <label>Replaced Image:</label>
                    <span>{item.replaceImageName}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'mediaAttachment' && item.mediaAction && item.mediaAction !== 'null' ? (
                  <p aria-label={`file type:${item.mediaAction}`} tabIndex={0}>
                    <label>File Type:</label>
                    <span>{item.mediaAction === 'replace' ? 'Replace File' : 'New File'}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'mediaAttachment' && item.mediaAction === 'replace' && item.replaceMediaName && item.replaceMediaName !== 'null' ? (
                  <p aria-label={`file replaced:${item.replaceMediaName}`} tabIndex={0}>
                    <label>Replaced File:</label>
                    <span>{item.replaceMediaName}</span>
                  </p>
                ) : ''}
                </div>
              </div>
            ))
          ) : (
            <NodataFound />
          )}

        </section>
      </div>
    </div>
  );
}

export default Attachment_Sidebar;

